/**
 * src/pages/Account.jsx — the signed-in artist's own account. Any role.
 *
 * Settings.jsx is the studio's configuration and owner-only; this is the
 * per-person counterpart, so every artist has somewhere to change their own
 * password without needing the emailed reset link.
 */
import { useState } from 'react';

import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { ErrorBanner, PageHeader } from '../components/ui.jsx';

export default function Account() {
  const { artist } = useAuth();

  return (
    <>
      <PageHeader title="Account" subtitle={artist?.email} />
      <div className="stack-lg">
        <ChangePassword />
      </div>
    </>
  );
}

/**
 * Change-password form. The API reissues this browser's session cookie on
 * success and signs out every other device (see POST /api/auth/change-password),
 * so there's nothing to do afterwards but clear the fields and say so.
 *
 * @returns {JSX.Element}
 */
function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaved(false);
    if (newPassword !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2 style={{ marginBottom: 6 }}>Change password</h2>
      <p className="muted small" style={{ marginBottom: 14 }}>
        This signs you out on every other device.
      </p>
      <form className="stack" onSubmit={submit} style={{ maxWidth: 420 }}>
        <ErrorBanner>{error}</ErrorBanner>
        {saved ? <div className="alert alert-success">Your password has been changed.</div> : null}
        <div>
          <label htmlFor="current-password">Current password</label>
          <input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>
        <div>
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label htmlFor="confirm-password">Confirm new password</label>
          <input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  );
}
